import { useCallback, useEffect, useRef } from 'react';

import { ITwoDragItemBase, ITwoDragListProps } from './types';

export interface ITwoDragSessionHandlers<TItem extends ITwoDragItemBase> {
  handleDragStart: () => void;
  handleDragEnd: () => void;
  handleTopSetList: (nextTopItems: TItem[]) => void;
  handleBottomSetList: (nextBottomItems: TItem[]) => void;
}

/**
 * TwoDragList：极简拖拽会话（只在 DragEnd 决策 onChange）。
 *
 * 交互契约（本 Hook 只负责 onChange 触发时机，不负责“能否移动”的策略判定）：
 * - 拖拽进行中：永远不触发 onChange（即使 setList 被多次调用）。
 * - 拖拽结束：只对比“拖拽开始时的顺序”与“结束时的最终顺序”，相同则不触发 onChange。
 *
 * 为什么需要在 onStart 记录基准顺序：
 * - ReactSortable 会在拖拽过程中多次调用 setList（同框排序/跨框移动都会出现）。
 * - 如果不记录 start 的基准，结束时很容易拿到已被 setList 影响过的值，从而导致“变化判定失真”。
 *
 * 为什么这里不使用组件 state：
 * - TwoDragList 的 UI 跟手排序由 SortableJS 直接操作 DOM 完成；
 * - 组件仅在拖拽结束后，把最终结果一次性上抛给受控方（由受控方更新 props 完成最终一致性渲染）。
 */
export function useTwoDragSession<TItem extends ITwoDragItemBase>(
  topItems: TItem[],
  bottomItems: TItem[],
  onChange: ITwoDragListProps<TItem>['onChange'],
): ITwoDragSessionHandlers<TItem> {
  // ===== 最新 props 引用：避免 onEnd 闭包拿到旧值 =====
  const latestTopItemsRef = useRef<TItem[]>(topItems);
  const latestBottomItemsRef = useRef<TItem[]>(bottomItems);

  useEffect(() => {
    latestTopItemsRef.current = topItems;
  }, [topItems]);

  useEffect(() => {
    latestBottomItemsRef.current = bottomItems;
  }, [bottomItems]);

  // ===== 本次拖拽会话：起始基准（只存 id 顺序，避免对象引用/字段变化干扰） =====
  const startTopIdsRef = useRef<Array<TItem['id']> | null>(null);
  const startBottomIdsRef = useRef<Array<TItem['id']> | null>(null);

  // ===== 本次拖拽会话：最终结果草稿（来自 ReactSortable 的 setList）=====
  const draftTopItemsRef = useRef<TItem[] | null>(null);
  const draftBottomItemsRef = useRef<TItem[] | null>(null);

  // ===== 防御性标记：避免异常情况下重复 end 触发 =====
  const draggingRef = useRef<boolean>(false);

  const toIdList = useCallback((items: TItem[]): Array<TItem['id']> => items.map((item) => item.id), []);

  const isSameIdOrder = useCallback(
    (a: Array<TItem['id']>, b: Array<TItem['id']>): boolean => {
      if (a.length !== b.length) return false;
      return a.every((id, index) => String(id) === String(b[index]));
    },
    [],
  );

  const handleDragStart = useCallback((): void => {
    draggingRef.current = true;

    const currentTopItems = latestTopItemsRef.current;
    const currentBottomItems = latestBottomItemsRef.current;

    startTopIdsRef.current = toIdList(currentTopItems);
    startBottomIdsRef.current = toIdList(currentBottomItems);

    // 清空上一轮残留，避免“同一次拖拽未触发 setList”时误用旧草稿。
    draftTopItemsRef.current = null;
    draftBottomItemsRef.current = null;
  }, [toIdList]);

  const handleDragEnd = useCallback((): void => {
    if (!draggingRef.current) {
      return;
    }
    draggingRef.current = false;

    const startTopIds = startTopIdsRef.current;
    const startBottomIds = startBottomIdsRef.current;
    startTopIdsRef.current = null;
    startBottomIdsRef.current = null;

    // 理论上 onStart 一定会先于 onEnd；如果出现异常顺序，直接放弃本次输出，交给受控方保持原值。
    if (!startTopIds || !startBottomIds) {
      draftTopItemsRef.current = null;
      draftBottomItemsRef.current = null;
      return;
    }

    const finalTopItems = draftTopItemsRef.current ?? latestTopItemsRef.current;
    const finalBottomItems = draftBottomItemsRef.current ?? latestBottomItemsRef.current;

    draftTopItemsRef.current = null;
    draftBottomItemsRef.current = null;

    const finalTopIds = toIdList(finalTopItems);
    const finalBottomIds = toIdList(finalBottomItems);

    const isTopSame = isSameIdOrder(startTopIds, finalTopIds);
    const isBottomSame = isSameIdOrder(startBottomIds, finalBottomIds);
    if (isTopSame && isBottomSame) {
      return;
    }

    onChange({
      topItems: finalTopItems,
      bottomItems: finalBottomItems,
    });
  }, [isSameIdOrder, onChange, toIdList]);

  const handleTopSetList = useCallback((nextTopItems: TItem[]): void => {
    // 拖拽进行中：只记录“最新草稿”，不触发 onChange。
    if (!draggingRef.current) {
      return;
    }
    draftTopItemsRef.current = nextTopItems;
  }, []);

  const handleBottomSetList = useCallback((nextBottomItems: TItem[]): void => {
    if (!draggingRef.current) {
      return;
    }
    draftBottomItemsRef.current = nextBottomItems;
  }, []);

  return {
    handleDragStart,
    handleDragEnd,
    handleTopSetList,
    handleBottomSetList,
  };
}

